import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Money } from "@/components/Money";
import { useSource } from "@/context/SourceContext";
import { fmtDate, monthLabel } from "@/lib/format";
import { moneyNode } from "@/components/Money";
import { cn } from "@/lib/utils";

const kfmt = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`);
const tip = { formatter: (v) => moneyNode(v), contentStyle: { fontSize: 12, borderRadius: 6 } };
const axis = { tick: { fontSize: 11, fill: "#64748B" }, tickLine: false };

export const Card = ({ title, subtitle, actions, children, testid }) => (
  <section className="rise-in rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={testid}>
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div><h2 className="text-base font-semibold text-slate-900 md:text-lg">{title}</h2>{subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}</div>
      {actions}
    </div>
    {children}
  </section>
);

export const Toggle = ({ value, options, onChange, testid }) => (
  <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid={testid}>
    {options.map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
        className={cn("rounded px-3 py-1 text-xs font-medium transition-colors", value === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900")}>{l}</button>
    ))}
  </div>
);

export const ErpSalesTrend = ({ month }) => {
  const [g, setG] = useState("monthly");
  const [pts, setPts] = useState(null);
  const src = useSource();
  useEffect(() => {
    src.get("/erp/sales/trend", { params: { granularity: g, month } }).then((r) => setPts(r.data.points)).catch(() => setPts([]));
  }, [src, g, month]);
  const data = (pts || []).map((p) => ({ label: g === "monthly" ? monthLabel(p.period, true) : p.period.slice(8), sales: p.sales }));
  return (
    <Card title="Sales trend" subtitle={g === "monthly" ? "Net sales (ex-VAT, returns netted) per month" : `Daily sales · ${monthLabel(month)}`} testid="erp-sales-trend"
      actions={<Toggle value={g} onChange={setG} testid="erp-sales-toggle" options={[["monthly", "Monthly"], ["daily", "Daily"]]} />}>
      {pts && pts.length === 0 ? <div className="ledger-grid flex h-64 items-center justify-center text-sm text-slate-500" data-testid="erp-sales-empty">No sales for this period</div> : (
        <div className="h-64" data-testid="erp-sales-chart" data-points={data.length}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" {...axis} minTickGap={8} /><YAxis tickFormatter={kfmt} {...axis} axisLine={false} width={44} />
              <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" name="Sales" fill="#0D9488" radius={[2, 2, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

const AGING_LABELS = { curr: "Current", a30: "≤30", a61: "≤61", a92: "≤92", a122: "≤122", a153: "≤153", a183: "≤183", a183p: ">183" };

export const OutstandingCard = () => {
  const [d, setD] = useState(null);
  const src = useSource();
  useEffect(() => { src.get("/erp/outstanding/summary").then((r) => setD(r.data)).catch(() => setD({ as_of: null })); }, [src]);
  if (!d) return null;
  if (!d.as_of) return <Card title="Outstanding receivables" testid="outstanding-card"><p className="text-sm text-slate-500" data-testid="outstanding-empty">No Outstanding report uploaded yet.</p></Card>;
  const base = d.aging.reduce((s, a) => s + a.amount, 0) || 1;
  return (
    <Card title="Outstanding receivables" subtitle={`As of ${fmtDate(d.as_of)} · ${d.customers} customers · not part of P&L`} testid="outstanding-card">
      <Money value={d.os} className="block text-2xl font-semibold text-slate-900" data-testid="outstanding-total" />
      <div className="mt-1 text-xs text-slate-500">PDC held <Money value={d.pdc} /> · uncovered <Money value={d.uncovered} /> · {d.negative} credit balances</div>
      <div className="mt-4 space-y-1.5" data-testid="outstanding-aging">
        {d.aging.map((a) => (
          <div key={a.key} className="flex items-center gap-3 text-xs" data-testid={`aging-${a.key}`}>
            <span className="w-14 text-slate-600">{AGING_LABELS[a.key]}</span>
            <div className="h-2 flex-1 overflow-hidden rounded bg-slate-100"><div className={cn("h-full", a.key === "a183p" ? "bg-red-500" : "bg-teal-600")} style={{ width: `${Math.max(0, (a.amount / base) * 100)}%` }} /></div>
            <Money value={a.amount} className="w-32 text-right" />
          </div>
        ))}
      </div>
      <div className="mt-5 h-28" data-testid="outstanding-trend" data-points={d.trend.length}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={d.trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="as_of" tickFormatter={fmtDate} {...axis} /><YAxis tickFormatter={kfmt} {...axis} axisLine={false} width={44} domain={[0, "auto"]} />
            <Tooltip {...tip} /><Line dataKey="os" name="Outstanding" stroke="#0D9488" strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {d.trend.length === 1 && <p className="text-xs text-slate-500" data-testid="outstanding-single-note">Trend builds as you upload more Outstanding reports</p>}
    </Card>
  );
};
